/**
 * Pure derivations for the listing page: name search, sort, and the page slice.
 */

import { TableSortState } from 'types/DesignSystem/Table';

import { CardListingRow, ProductCard } from '../types';

const matchesName = (card: ProductCard, needle: string): boolean =>
  card.name.toLowerCase().includes(needle);

/** Cards whose name contains the search text (spec R20). */
export const selectMatchingCards = (
  cards: ProductCard[],
  searchTerm: string
): ProductCard[] => {
  const needle = searchTerm.trim().toLowerCase();
  if (needle === '') {
    return cards;
  }
  return cards.filter((card) => matchesName(card, needle));
};

/** Number of listing pages; at least 1, so a current page always exists. */
export const selectListingPageCount = (total: number, showCount: number): number =>
  Math.max(1, Math.ceil(Math.max(0, total) / Math.max(1, showCount)));

/**
 * One page of rows (spec R23 + pagination). `page` is 1-based and out-of-range
 * values clamp into range: deleting the last row of the last page must land
 * the view on the new last page, never on an empty one.
 */
export const selectListingPage = (
  cards: ProductCard[],
  page: number,
  showCount: number
): ProductCard[] => {
  const perPage = Math.max(1, showCount);
  const clamped = Math.min(
    Math.max(1, page),
    selectListingPageCount(cards.length, perPage)
  );
  return cards.slice((clamped - 1) * perPage, clamped * perPage);
};

export const selectCardById = (
  cards: ProductCard[],
  cardId: string
): ProductCard | undefined => cards.find((card) => card.id === cardId);

/**
 * The listing's sort (Guide-table parity, operator 2026-08-18). Names compare
 * case-insensitively so "zeta" does not sort before "Alpha"; timestamps are
 * ISO-8601, which orders correctly as plain strings. A null direction returns
 * the input order untouched (the repository's newest-first).
 */
export const sortCardsForListing = (
  cards: ProductCard[],
  sortState: TableSortState
): ProductCard[] => {
  if (sortState.direction === null) {
    return cards;
  }
  const factor = sortState.direction === 'asc' ? 1 : -1;
  const compare =
    sortState.columnId === 'name'
      ? (a: ProductCard, b: ProductCard) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      : // A stored card has no createdAt of its own, so the local adapters
        // sort createdAt as updatedAt — the closest honest order they hold.
        (a: ProductCard, b: ProductCard) => a.updatedAt.localeCompare(b.updatedAt);
  return [...cards].sort((a, b) => factor * compare(a, b));
};

/**
 * A stored card as a listing row, for adapters that hold whole cards (browser
 * storage, in-memory). They have no authorship record — those fields stay ''.
 */
export const listingRowFromCard = (card: ProductCard): CardListingRow => ({
  id: card.id,
  name: card.name,
  references: [...card.references],
  updatedAt: card.updatedAt,
  createdAt: '',
  createdBy: '',
});
